import { Alert, Box, Button, Card, Group, Loader, SegmentedControl, SimpleGrid, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { AdPlatform } from '@marketing-tool/shared';
import type { GetROASResult, ROASPartitionRow } from '@marketing-tool/shared';
import dayjs from 'dayjs';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router';

import { PageWide } from '../../components/PageLayout';
import { Table } from '../../components/Table';
import type { TableColumn } from '../../components/Table';
import { useCampaigns } from '../../data/game-report';
import type { AdGroup } from '../../data/game-report/types';
import type { Game } from '../../data/games';
import * as remote from '../../data/remote-data';
import { trpc } from '../../lib/trpc';

import { AdGroupSelector } from './AdGroupSelector';
import { CampaignFilterSelector } from './CampaignFilterSelector';
import { OSSelector } from './OSSelector';
import { PlatformSelector } from './PlatformSelector';

type ROASDateData = GetROASResult['resultsByDay'][number];
type ROASPartitionData = GetROASResult['resultsByCampaign'][number];

// ── Column factories ────────────────────────────────────────────────────

const ROAS_DAYS = [
    { field: 'revenueDay1', label: 'D1' },
    { field: 'revenueDay3', label: 'D3' },
    { field: 'revenueDay7', label: 'D7' },
    { field: 'revenueDay14', label: 'D14' },
    { field: 'revenueDay30', label: 'D30' },
    { field: 'revenueDay90', label: 'D90' },
] as const;

type ROASRow = ROASDateData | ROASPartitionData;

const REVENUE_FIELDS = [
    'revenueDay1',
    'revenueDay3',
    'revenueDay7',
    'revenueDay14',
    'revenueDay30',
    'revenueDay90',
] as const;

function computeTotals(rows: ROASDateData[]): ROASDateData {
    const totals = rows.reduce(
        (acc, row) => {
            acc.spend += row.spend;
            for (const field of REVENUE_FIELDS) {
                acc[field] += row[field];
            }
            return acc;
        },
        { spend: 0, revenueDay1: 0, revenueDay3: 0, revenueDay7: 0, revenueDay14: 0, revenueDay30: 0, revenueDay90: 0 },
    );
    return { day: 'total', ...totals };
}

function makeROASColumns<T extends ROASRow>(options?: { footer?: (data: T[]) => ROASRow }): TableColumn<T>[] {
    return ROAS_DAYS.map(({ field, label }) => ({
        name: `${label} (ROAS)`,
        type: 'percentage' as const,
        width: 80,
        render: (row: T) => {
            if (row.spend === 0) return '-';
            const roas = ((row[field as keyof T] as number) / row.spend) * 100;
            return `${roas.toFixed(2)}%`;
        },
        ...(options?.footer
            ? {
                  footer: (data: T[]) => {
                      const totals = options.footer!(data);
                      if (totals.spend === 0) return '-';
                      const roas = (totals[field as keyof ROASRow] / totals.spend) * 100;
                      return <strong>{`${roas.toFixed(2)}%`}</strong>;
                  },
              }
            : {}),
    }));
}

function makeRevenueColumns<T extends ROASRow>(options?: {
    prefix?: string;
    footer?: (data: T[]) => ROASRow;
}): TableColumn<T>[] {
    const prefix = options?.prefix ?? '';
    return ROAS_DAYS.map(({ field, label }) => ({
        name: `${label} (${prefix}Revenue)`,
        type: 'currency' as const,
        width: 95,
        hidden: true,
        render: (row: T) => `¥${Math.round(row[field as keyof T] as number).toLocaleString()}`,
        ...(options?.footer
            ? {
                  footer: (data: T[]) => {
                      const totals = options.footer!(data);
                      return <strong>{`¥${Math.round(totals[field as keyof ROASRow]).toLocaleString()}`}</strong>;
                  },
              }
            : {}),
    }));
}

// ── Component ───────────────────────────────────────────────────────────

export const ROASTab = () => {
    const { game, gameId } = useOutletContext<{ game: Game; gameId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const selectedCampaignsFromUrl = useMemo(() => {
        const param = searchParams.get('campaigns');
        return param ? param.split(',').filter(Boolean) : [];
    }, [searchParams]);

    const selectedOSFiltersFromUrl = useMemo(() => {
        const param = searchParams.get('osFilters');
        if (!param) return ['ios', 'android', 'other']; // Default to all
        const validOSValues = ['ios', 'android', 'other'];
        const parsed = param.split(',').filter((v) => validOSValues.includes(v));
        return parsed.length > 0 ? parsed : ['ios', 'android', 'other']; // Default to all if no valid values
    }, [searchParams]);

    const [platform, setPlatform] = useState<AdPlatform | undefined>(game?.platforms[0]);
    const [selectedCampaigns, setSelectedCampaignsState] = useState<string[]>(selectedCampaignsFromUrl);
    const [selectedOSFilters, setSelectedOSFiltersState] = useState<string[]>(selectedOSFiltersFromUrl);
    const [adGroup, setAdGroup] = useState<AdGroup | undefined>();
    const [from, setFrom] = useState<dayjs.Dayjs | undefined>(dayjs().subtract(6, 'days'));
    const [to, setTo] = useState<dayjs.Dayjs | undefined>(dayjs());
    const [fetchedFrom, setFetchedFrom] = useState<dayjs.Dayjs | undefined>();
    const [fetchedTo, setFetchedTo] = useState<dayjs.Dayjs | undefined>();
    const [roasResult, setRoasResult] = useState<GetROASResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [resultTab, setResultTab] = useState<string>('daily');

    useEffect(() => {
        setSelectedCampaignsState(selectedCampaignsFromUrl);
    }, [selectedCampaignsFromUrl]);

    useEffect(() => {
        setSelectedOSFiltersState(selectedOSFiltersFromUrl);
    }, [selectedOSFiltersFromUrl]);

    const setSelectedCampaigns = useCallback((campaigns: string[]) => {
        setSelectedCampaignsState(campaigns);
    }, []);

    const setSelectedOSFilters = useCallback((osFilters: string[]) => {
        setSelectedOSFiltersState(osFilters);
    }, []);

    const campaignsData = useCampaigns(gameId, platform ?? AdPlatform.Line);

    const singleCampaign = useMemo(() => {
        if (selectedCampaigns.length === 1) {
            const campaignList = remote.withDefault(campaignsData.data, []);
            return campaignList.find((c) => c.name === selectedCampaigns[0]);
        }
        return undefined;
    }, [selectedCampaigns, campaignsData.data]);

    useEffect(() => setPlatform(game?.platforms[0]), [game]);
    useEffect(() => {
        setSelectedCampaigns([]);
        setAdGroup(undefined);
    }, [game, platform, setSelectedCampaigns]);
    useEffect(() => setAdGroup(undefined), [selectedCampaigns]);

    // Reset result tab when available tabs change
    useEffect(() => {
        if (!roasResult) return;
        if (resultTab === 'campaign' && roasResult.resultsByCampaign.length === 0) setResultTab('daily');
        if (resultTab === 'adGroup' && roasResult.resultsByAdGroup.length === 0) setResultTab('daily');
    }, [roasResult, resultTab]);

    // Check if OS filter combination is unsupported (mixed "other" with ios/android)
    const hasUnsupportedOSCombination = useMemo(() => {
        if (selectedOSFilters.length === 0 || selectedOSFilters.length === 3) return false;
        const includesOther = selectedOSFilters.includes('other');
        const includesIOS = selectedOSFilters.includes('ios');
        const includesAndroid = selectedOSFilters.includes('android');
        return includesOther && (includesIOS || includesAndroid);
    }, [selectedOSFilters]);

    // Helper to format OS values for display
    const formatOSLabel = (os: string): string => {
        switch (os) {
            case 'ios':
                return 'iOS';
            case 'android':
                return 'Android';
            case 'other':
                return 'Other';
            default:
                return os;
        }
    };

    const isFormComplete = platform && from && to && selectedOSFilters.length > 0;

    const fetchROASData = async () => {
        if (!from || !to || !platform || selectedOSFilters.length === 0) return;

        const urlParams = new URLSearchParams(searchParams);
        if (selectedCampaigns.length > 0) {
            urlParams.set('campaigns', selectedCampaigns.join(','));
        } else {
            urlParams.delete('campaigns');
        }
        if (selectedOSFilters.length > 0 && selectedOSFilters.length < 3) {
            // Only persist in URL if not all selected (to keep URL clean)
            urlParams.set('osFilters', selectedOSFilters.join(','));
        } else {
            urlParams.delete('osFilters');
        }
        void navigate(`/games/${gameId}/roas?${urlParams.toString()}`, { replace: true });

        setFetchedFrom(from);
        setFetchedTo(to);
        setLoading(true);
        try {
            const fromStr = from.format('YYYY-MM-DD');
            const toStr = to.format('YYYY-MM-DD');
            const osFilters =
                selectedOSFilters.length > 0 && selectedOSFilters.length < 3 ? selectedOSFilters : undefined;
            const campaignNames = selectedCampaigns.length > 0 ? selectedCampaigns : undefined;

            const [resultsByDay, resultsByCampaign, resultsByAdGroup] = await Promise.all([
                trpc.games.amplitude.roas.byDay.query({
                    gameId,
                    from: fromStr,
                    to: toStr,
                    platform,
                    campaignNames,
                    adGroupName: adGroup?.name,
                    osFilters,
                }),
                // Fetch campaign partitions only if no specific campaigns selected
                !campaignNames
                    ? trpc.games.amplitude.roas.byCampaign.query({
                          gameId,
                          from: fromStr,
                          to: toStr,
                          platform,
                          osFilters,
                      })
                    : Promise.resolve([] as ROASPartitionRow[]),
                // Fetch ad group partitions only if campaigns selected but no ad group
                campaignNames && !adGroup?.name
                    ? trpc.games.amplitude.roas.byAdGroup.query({
                          gameId,
                          from: fromStr,
                          to: toStr,
                          platform,
                          campaignNames,
                          osFilters,
                      })
                    : Promise.resolve([] as ROASPartitionRow[]),
            ]);

            setRoasResult({ resultsByDay, resultsByCampaign, resultsByAdGroup });
        } catch (error) {
            console.error('Failed to fetch ROAS data:', error);
            setRoasResult(null);
        } finally {
            setLoading(false);
        }
    };

    const resultTabData = useMemo(() => {
        const data: { label: string; value: string }[] = [{ label: 'Daily', value: 'daily' }];
        if (roasResult?.resultsByCampaign.length) data.push({ label: 'By Campaign', value: 'campaign' });
        if (roasResult?.resultsByAdGroup.length) data.push({ label: 'By Ad Group', value: 'adGroup' });
        return data;
    }, [roasResult]);

    return (
        <PageWide>
            <Stack gap="lg">
                <Card withBorder>
                    <Card.Section
                        inheritPadding
                        py="md"
                        bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                    >
                        <Stack gap="md">
                            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                                <PlatformSelector
                                    id="platform"
                                    gameId={gameId}
                                    value={platform}
                                    onChange={setPlatform}
                                    label="Platform"
                                />

                                {platform && (
                                    <CampaignFilterSelector
                                        id="campaigns"
                                        gameId={gameId}
                                        platform={platform}
                                        value={selectedCampaigns}
                                        onChange={setSelectedCampaigns}
                                        label="Campaigns (Optional)"
                                        clearable
                                    />
                                )}

                                {platform && singleCampaign && (
                                    <AdGroupSelector
                                        id="ad-group"
                                        gameId={gameId}
                                        platform={platform}
                                        campaignId={singleCampaign.id}
                                        value={adGroup?.id}
                                        onChange={setAdGroup}
                                        label="Ad Group (Optional)"
                                        clearable
                                    />
                                )}

                                {platform && (
                                    <OSSelector
                                        id="os-filters"
                                        value={selectedOSFilters}
                                        onChange={setSelectedOSFilters}
                                        label="Operating System"
                                    />
                                )}

                                <DatePickerInput
                                    type="range"
                                    label="Date Range"
                                    allowSingleDateInRange
                                    value={[from?.toDate() ?? null, to?.toDate() ?? null]}
                                    onChange={([start, end]) => {
                                        setFrom(start ? dayjs(start) : undefined);
                                        setTo(end ? dayjs(end) : undefined);
                                    }}
                                />
                            </SimpleGrid>

                            {selectedOSFilters.length === 0 && (
                                <Alert color="blue" title="Operating system required">
                                    Please select at least one operating system to load ROAS data.
                                </Alert>
                            )}

                            <Group justify="flex-end">
                                <Button
                                    onClick={fetchROASData}
                                    disabled={!isFormComplete || loading}
                                    leftSection={loading ? <Loader size={20} /> : undefined}
                                >
                                    {loading ? 'Loading...' : 'Apply Filters'}
                                </Button>
                            </Group>
                        </Stack>
                    </Card.Section>
                </Card>

                {roasResult !== null && (
                    <Card withBorder p="md">
                        <Stack gap="md">
                            <Box>
                                <Text fw={600}>
                                    ROAS — {fetchedFrom?.format('MMM D')} to {fetchedTo?.format('MMM D, YYYY')}
                                    {selectedCampaigns.length === 1 && ` — ${selectedCampaigns[0]}`}
                                    {selectedCampaigns.length > 1 && ` — ${selectedCampaigns.length} campaigns`}
                                    {adGroup && ` — ${adGroup.name}`}
                                    {selectedOSFilters.length < 3 &&
                                        !hasUnsupportedOSCombination &&
                                        ` — ${selectedOSFilters.map(formatOSLabel).join(', ')}`}
                                </Text>
                                {hasUnsupportedOSCombination && (
                                    <Alert color="yellow" mt="xs" title="OS filter limitation">
                                        Filtering by "Other" combined with iOS or Android is not supported due to
                                        Amplitude API limitations. All OS data is included in the results below.
                                    </Alert>
                                )}
                            </Box>

                            {loading ? (
                                <Box ta="center" py="xl">
                                    <Loader />
                                    <Text c="dimmed" mt="xs">
                                        Loading ROAS data...
                                    </Text>
                                </Box>
                            ) : roasResult.resultsByDay.length > 0 ? (
                                <>
                                    {resultTabData.length > 1 && (
                                        <SegmentedControl
                                            value={resultTab}
                                            onChange={setResultTab}
                                            size="sm"
                                            data={resultTabData}
                                        />
                                    )}
                                    {resultTab === 'daily' && <DailyROASTable data={roasResult.resultsByDay} />}
                                    {resultTab === 'campaign' && roasResult.resultsByCampaign.length > 0 && (
                                        <CampaignROASTable
                                            data={roasResult.resultsByCampaign}
                                            onSelectCampaign={(name) => setSelectedCampaigns([name])}
                                        />
                                    )}
                                    {resultTab === 'adGroup' && roasResult.resultsByAdGroup.length > 0 && (
                                        <AdGroupROASTable data={roasResult.resultsByAdGroup} />
                                    )}
                                </>
                            ) : (
                                <Text c="dimmed" ta="center" py="xl">
                                    No ROAS data available for the selected period.
                                </Text>
                            )}
                        </Stack>
                    </Card>
                )}
            </Stack>
        </PageWide>
    );
};

// ── Sub-components ──────────────────────────────────────────────────────

const DailyROASTable = ({ data }: { data: ROASDateData[] }) => {
    const footerCompute = useCallback((rows: ROASDateData[]) => computeTotals(rows), []);

    return (
        <Table
            storageKey="roas-daily"
            id={(row) => row.day}
            data={data}
            density="sm"
            pagination={{ pageSize: 10 }}
            columns={[
                {
                    name: 'Date',
                    type: 'date',
                    render: (row: ROASDateData) => dayjs(row.day).format('MMM D, YYYY'),
                    footer: () => <strong>Total</strong>,
                },
                {
                    name: 'Spend',
                    type: 'currency',
                    width: 100,
                    render: (row: ROASDateData) => `¥${Math.round(row.spend).toLocaleString()}`,
                    footer: (rows: ROASDateData[]) => {
                        const totals = computeTotals(rows);
                        return <strong>{`¥${Math.round(totals.spend).toLocaleString()}`}</strong>;
                    },
                },
                ...makeROASColumns<ROASDateData>({ footer: footerCompute }),
                ...makeRevenueColumns<ROASDateData>({ footer: footerCompute }),
            ]}
        />
    );
};

const CampaignROASTable = ({
    data,
    onSelectCampaign,
}: {
    data: ROASPartitionData[];
    onSelectCampaign: (name: string) => void;
}) => {
    return (
        <Table
            storageKey="roas-campaigns"
            id={(row) => row.name}
            data={data}
            density="sm"
            pagination={{ pageSize: 10 }}
            columns={[
                {
                    name: 'Campaign',
                    render: (row: ROASPartitionData) => (
                        <Button
                            type="button"
                            variant="subtle"
                            onClick={() => onSelectCampaign(row.name)}
                            p={0}
                            styles={{ root: { minWidth: 0 } }}
                        >
                            {row.name}
                        </Button>
                    ),
                },
                {
                    name: 'Total Spend',
                    type: 'currency',
                    width: 100,
                    render: (row: ROASPartitionData) => `¥${Math.round(row.spend).toLocaleString()}`,
                },
                ...makeROASColumns<ROASPartitionData>(),
                ...makeRevenueColumns<ROASPartitionData>({ prefix: 'Total ' }),
            ]}
        />
    );
};

const AdGroupROASTable = ({ data }: { data: ROASPartitionData[] }) => {
    return (
        <Table
            storageKey="roas-ad-groups"
            id={(row) => row.name}
            data={data}
            density="sm"
            pagination={{ pageSize: 10 }}
            columns={[
                {
                    name: 'Ad Group',
                    render: (row: ROASPartitionData) => (
                        <Text span truncate title={row.name}>
                            {row.name}
                        </Text>
                    ),
                },
                {
                    name: 'Total Spend',
                    type: 'currency',
                    width: 100,
                    render: (row: ROASPartitionData) => `¥${Math.round(row.spend).toLocaleString()}`,
                },
                ...makeROASColumns<ROASPartitionData>(),
                ...makeRevenueColumns<ROASPartitionData>({ prefix: 'Total ' }),
            ]}
        />
    );
};
